import React, { useContext, useState } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { TFileExtension } from 'types';
import Button from 'components/Button/Button';
import Text from 'components/Text/Text';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import { postMediaFile } from 'backend/additionalInformation';
import { createAppError } from 'utils/helpers';
import { openBrowseFile } from 'utils/openBrowseFile';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import classNames from 'classnames';
import style from './FileSection.module.scss';

interface IProps {
	onUploadFile?: ({
		fileId,
		fileHash,
		sourceUrl,
	}: {
		fileId: string;
		fileHash: Nullable<string>;
		sourceUrl?: string;
	}) => void;
	onDeleteFile?: () => void;
	onSizeError?: () => void;
	onFileTypeError?: () => void;
	fileId: Nullable<string>;
	filePath: Nullable<string>;
	fileName: string;
	className?: string;
	maxFileSizeMB?: number;
	isDisabledControls?: boolean;
	requiredValidationMessage?: string;
	accept?: TFileExtension[];
}

export const FileSection: React.FC<IProps> = React.memo(
	({
		onUploadFile,
		onDeleteFile,
		onSizeError,
		onFileTypeError,
		fileId,
		filePath,
		fileName,
		className,
		maxFileSizeMB = 1,
		isDisabledControls = false,
		requiredValidationMessage,
		accept = ['jpg', 'jpeg', 'png', 'mp4', 'pdf', 'zip', 'bin', 'apk', 'html', 'json', 'p12'],
	}) => {
		const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
		const [localFileName, setLocalFileName] = useState(fileName);
		const { t } = useTranslation('error');
		const containerClasses = classNames(style.container, className);
		const hasFile = fileId || (fileId !== null && filePath);

		const sendForm = async (files: FileList | null) => {
			if (files) {
				if (!accept.includes(files[0].name.toLowerCase().split('.').pop() as TFileExtension)) {
					onFileTypeError && onFileTypeError();
					return;
				}

				const file = files[0];

				if (file.size > 1000 * 1000 * maxFileSizeMB && onSizeError) {
					onSizeError();
					return;
				}

				const formData = new FormData();
				formData.append('file', file, file.name);
				// const sourceUrl = URL.createObjectURL(file);
				try {
					showLoader();
					const { data } = await postMediaFile({ data: formData });
					// onUploadFile && onUploadFile(data.fileId, sourceUrl);
					onUploadFile && onUploadFile({ fileId: data.fileId, fileHash: data.fileHash });
					setLocalFileName(file.name);
				} catch (err) {
					const error = err as Error;
					const appError = createAppError(error);
					addAppError(appError);
				} finally {
					hideLoader();
				}
			} else {
				addAppError({
					id: uuidv4(),
					message: t('sorryWeDoNotSupportThatTypeOfFile'),
					name: 'noErrorData',
				});
			}
		};

		const handleUpload = () => {
			openBrowseFile({
				onChoose: sendForm,
				accept: accept.map((extension) => '.' + extension).join(', '),
			});
		};

		// const handleShow = async () => {
		// 	const link = document.createElement('a');
		// 	link.target = '_blank';
		// 	if (hasFile && item.sourceUrl) {
		// 		link.href = item.sourceUrl;
		// 	} else if (hasFile && item.filePath) {
		// 		try {
		// 			showLoader();
		// 			const { data } = await getFilePathForMediaFilesWithSizeForStyleSettings(
		// 				item.translationId || '',
		// 				'Original',
		// 			);
		// 			link.href = data.preSignedUrl || '';
		// 		} catch (err) {
		// 			const error = err as Error;
		// 			const appError = createAppError(error);
		// 			addAppError(appError);
		// 			return;
		// 		} finally {
		// 			hideLoader();
		// 		}
		// 	}
		// 	link.click();
		// };

		return (
			<>
				<div className={containerClasses} data-test="PDFSection">
					{(hasFile || (!onUploadFile && filePath)) && <div className={style.fileName}>{localFileName}</div>}
					{onUploadFile && (
						<Button
							theme="secondaryPurple"
							icon={
								<svg className={style.icon}>
									<use xlinkHref={`${breadcrumbs}#${hasFile ? 'reload' : 'uploadPhoto'}`} />
								</svg>
							}
							disabled={isDisabledControls}
							onClick={handleUpload}>
							<Text
								translationNameSpace="form"
								translationKey={hasFile ? 'replaceClientTLSCertificateButton' : 'uploadButton'}
								asFragment={true}
							/>
						</Button>
					)}
					{/* <Button
					theme="secondaryPurple"
					icon={
						<svg className={style.icon}>
							<use xlinkHref={`${breadcrumbs}#viewDocument`} />
						</svg>
					}
					onClick={handleShow}
					disabled={!hasFile}>
					<Text translationNameSpace="form" translationKey="viewDocumentButton" asFragment={true} />
				</Button> */}
					{onDeleteFile && hasFile && (
						<Button
							className={style.deleteButton}
							theme="secondary"
							icon={
								<svg className={style.deleteIcon}>
									<use xlinkHref={`${breadcrumbs}#bin`} />
								</svg>
							}
							disabled={isDisabledControls}
							onClick={onDeleteFile}>
							<Text translationNameSpace="form" translationKey="deleteButton" asFragment={true} />
						</Button>
					)}
				</div>
				{requiredValidationMessage && (
					<Text
						className={style.requiredValidationError}
						translationNameSpace="error"
						translationKey={requiredValidationMessage}
						theme="error"
						size="s"
					/>
				)}
			</>
		);
	},
);
